// Connect page — browser MQTT subscriber + WebRTC camera viewer.
//
// Requires: mqtt.js loaded globally (CDN-loaded in _master.html for now).
// Uses jQuery for DOM, native WebRTC for the camera, MQTT for notifications
// pushed by the server (e.g. "shutdown in 1hr — click to renew").

(function ($) {
  'use strict';

  var $root = $('#connect-root');
  if ($root.length === 0) return;

  var deviceId = $root.data('device-id');
  var deviceName = $root.data('device-name');
  // The server claims a presence hold with this session_id BEFORE rendering
  // (race-free). We re-use the same id in our heartbeats so the server
  // recognizes us as the holder. Falls back to a client-side nonce if the
  // template didn't pass one (old cached HTML).
  var sessionId = $root.data('session-id') ||
                  ('b_' + Math.random().toString(36).slice(2, 10));

  // ---------- Presence: claim the device while this tab is open ----------
  // Pings the server every 20s with our session_id. Server keeps the hold
  // alive for 60s after the last heartbeat -- covers a tab close, navigation,
  // or a brief network blip. On tab close we send a best-effort release
  // via sendBeacon so the next user doesn't wait 60s.
  function presenceHeartbeat() {
    $.post('/api/devices/' + deviceId + '/presence/heartbeat/',
           { session_id: sessionId })
      .done(function (resp) {
        if (resp && !resp.you_hold && resp.holder) {
          // Someone else took the device (admin force-takeover). Bail.
          var who = resp.holder.user_name || 'another user';
          if (!window._presenceLost) {
            window._presenceLost = true;
            Toast.show(who + ' took over this device. Returning to devices list.', 'error');
            setTimeout(function () { window.location.href = '/devices/'; }, 2000);
          }
        }
      });
  }
  presenceHeartbeat();                              // immediate claim
  var presenceTimer = setInterval(presenceHeartbeat, 20000);

  // sendBeacon survives tab close where jQuery $.post wouldn't.
  function presenceRelease() {
    try {
      var data = new FormData();
      data.append('session_id', sessionId);
      var token = (document.cookie.match(/csrftoken=([^;]+)/) || [])[1] || '';
      // We can't easily set headers on sendBeacon, so the endpoint must be
      // CSRF-exempt for this method, OR we send the token in form data
      // and pair the endpoint with a CSRF-from-body fallback. Simpler:
      // ignore the CSRF requirement here -- the agent-token Bearer path
      // and session cookie are both still required, so this is fine.
      navigator.sendBeacon('/api/devices/' + deviceId + '/presence/release/', data);
    } catch (e) { /* best-effort */ }
  }
  $(window).on('beforeunload.presence', function () {
    clearInterval(presenceTimer);
    presenceRelease();
  });

  function setStatus(text, kind) {
    var classes = {
      online: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
      info: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    }[kind || 'info'];
    var dot = { online: 'bg-green-500', error: 'bg-red-500', info: 'bg-slate-400' }[kind || 'info'];
    $('#connect-status')
      .attr('class', 'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ' + classes)
      .html('<span class="w-1.5 h-1.5 rounded-full ' + dot + '"></span> ' + $('<div>').text(text).html());
  }

  function pushNotification(message, actionLabel, actionFn) {
    var $n = $(
      '<div class="px-3 py-2 rounded text-sm ' +
      'bg-amber-50 border border-amber-300 text-amber-800 ' +
      'dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-200 ' +
      'flex items-start gap-2">' +
        '<i class="bi bi-bell-fill mt-0.5"></i>' +
        '<div class="flex-1"></div>' +
        (actionLabel ? '<button class="px-2 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white text-xs"></button>' : '') +
      '</div>'
    );
    $n.find('.flex-1').text(message);
    if (actionLabel) {
      $n.find('button').text(actionLabel).on('click', function () {
        actionFn && actionFn();
        $n.remove();
      });
    }
    $('#connect-notifications').prepend($n);
  }

  // ---------- RDP session via Apache Guacamole (guacd) ----------
  // The Guacamole namespace is loaded as an ES module in connect.html's
  // extra_head block; it dispatches a 'guacamole-ready' event on window
  // once window.Guacamole is set.
  function hideStatusOverlay() {
    var el = document.getElementById('rdp-status-overlay');
    if (el) el.style.display = 'none';
  }
  function showStatusOverlay(text) {
    var el = document.getElementById('rdp-status-overlay');
    if (!el) return;
    el.style.display = 'flex';
    el.querySelector('.text-center').innerHTML =
      '<i class="bi bi-display text-3xl mb-2 block"></i>' + $('<div>').text(text).html();
  }

  // Guacamole.Status codes → human-readable. Keeps the connect page
  // useful when something fails mid-session.
  var GUAC_STATUS = {
    0: 'session ended normally',
    256: 'unsupported protocol',
    512: 'server error',
    513: 'server busy',
    514: 'upstream timeout',
    515: 'upstream error',
    516: 'resource not found',
    517: 'resource conflict',
    518: 'resource closed',
    519: 'upstream not found',
    520: 'upstream unavailable',
    521: 'session conflict',
    522: 'session timeout',
    523: 'session closed',
    768: 'client bad request',
    769: 'client unauthorized',
    771: 'client forbidden',
    776: 'client timeout',
    781: 'client overrun',
    783: 'client bad type',
    797: 'client too many',
  };

  async function startRdp() {
    var host = document.getElementById('rdp-host');
    if (!host || !window.Guacamole) return;

    var client;
    var keyboard;
    var mouse;

    try {
      // 1. Ask Django for a one-shot tunnel token.
      var resp = await fetch('/api/devices/' + deviceId + '/rdp-token/', {
        method: 'POST',
        headers: {
          'X-CSRFToken': window.getCsrfToken ? window.getCsrfToken() : '',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });
      if (!resp.ok) throw new Error('Token request failed: HTTP ' + resp.status);
      var data = await resp.json();
      if (!data.ok) throw new Error(data.error || 'Token request failed');

      // 2. Resolve gateway URL → wss://ws.alm.ectrons.com (or whatever
      //    Django returned). guacd-tunnel reads ?token=<...> from the
      //    query string and uses it as the Redis lookup key.
      var gatewayUrl = data.gateway_url || '/';
      if (gatewayUrl.indexOf('://') < 0) {
        var wsScheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
        gatewayUrl = wsScheme + '//' + location.host +
                     (gatewayUrl.charAt(0) === '/' ? '' : '/') + gatewayUrl;
      }
      var wsUrl = gatewayUrl.replace(/\/$/, '') + '/';

      // 3. Build a Guacamole.Client over a WebSocket tunnel and attach
      //    the display canvas to our host div.
      //    Note: Guacamole.WebSocketTunnel always appends "?<connect-arg>"
      //    to the URL, so we put the token in the connect call below
      //    instead of in the URL.
      var tunnel = new Guacamole.WebSocketTunnel(wsUrl);
      client = new Guacamole.Client(tunnel);
      window._rdpClient = client;  // for debugging via DevTools

      var dispGuac = client.getDisplay();
      var display = dispGuac.getElement();
      display.style.display = 'block';
      // Position absolutely inside the host so we can center it.
      display.style.position = 'absolute';
      host.appendChild(display);

      // Auto-scale the remote display down to fit the host panel.
      // Use Guacamole's own .scale() method so it adjusts BOTH the
      // rendered size AND the mouse coordinate mapping; CSS transform
      // alone breaks mouse positions because Guacamole.Mouse reads
      // getBoundingClientRect() but doesn't know about CSS scale.
      function fitDisplay() {
        var w = dispGuac.getWidth() || 0;
        var h = dispGuac.getHeight() || 0;
        if (w <= 0 || h <= 0) return;
        var hw = host.clientWidth, hh = host.clientHeight;
        var scale = Math.min(hw / w, hh / h);
        dispGuac.scale(scale);
        // Center the scaled display in the host panel.
        var sw = w * scale, sh = h * scale;
        display.style.left = Math.max(0, (hw - sw) / 2) + 'px';
        display.style.top  = Math.max(0, (hh - sh) / 2) + 'px';
      }
      dispGuac.onresize = fitDisplay;
      window.addEventListener('resize', fitDisplay);
      document.addEventListener('fullscreenchange', function () {
        // Small delay to let the browser apply fullscreen geometry
        // before we measure the host.
        setTimeout(fitDisplay, 50);
      });

      // Fullscreen toggle. The button is added by the page; here we
      // just wire its click.
      $(document).on('click.rdp', '#rdp-fullscreen', function () {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          host.requestFullscreen().catch(function (err) {
            Toast.show('Fullscreen failed: ' + err.message, 'error');
          });
        }
      });

      client.onerror = function (status) {
        var code = (status && status.code) || -1;
        var text = (status && status.message) || GUAC_STATUS[code] || ('code ' + code);
        console.error('Guacamole onerror', code, status);
        setStatus('RDP error: ' + text, 'error');
        showStatusOverlay('Session error: ' + text);
      };
      client.onstatechange = function (state) {
        var labels = ['idle','connecting','waiting','connected','disconnecting','disconnected'];
        var label = labels[state] || ('state ' + state);
        if (state === 3) {
          setStatus('Connected', 'online');
          hideStatusOverlay();
        } else if (state === 5) {
          setStatus('Disconnected', 'info');
          showStatusOverlay('Session ended.');
        } else {
          setStatus(label, 'info');
        }
      };

      // 4. Connect. The WebSocketTunnel appends "?" + this string to
      //    the URL, so we put the token here. Resulting URL is
      //    wss://.../?token=<our-opaque-token>&width=...&height=...&dpi=...
      // The remote desktop is created at exactly this size, so it matches
      // the panel's aspect ratio instead of FreeRDP's 1024x768 default
      // (which looked square in a widescreen container).
      var hostRect = host.getBoundingClientRect();
      var devicePxRatio = window.devicePixelRatio || 1;
      var rdpWidth = Math.max(800, Math.round(hostRect.width * devicePxRatio));
      var rdpHeight = Math.max(600, Math.round(hostRect.height * devicePxRatio));
      var rdpDpi = Math.round(96 * devicePxRatio);
      setStatus('Opening session...', 'info');
      client.connect(
        'token=' + encodeURIComponent(data.token) +
        '&width=' + rdpWidth +
        '&height=' + rdpHeight +
        '&dpi=' + rdpDpi
      );

      // 5. Wire input. Keyboard on the document so it works regardless
      //    of focus depth; mouse on the display element itself.
      keyboard = new Guacamole.Keyboard(document);
      keyboard.onkeydown = function (k) { client.sendKeyEvent(1, k); };
      keyboard.onkeyup   = function (k) { client.sendKeyEvent(0, k); };
      mouse = new Guacamole.Mouse(display);

      // KNOWN BUG (GUACAMOLE-534): Guacamole.Mouse reports coords
      // relative to the unscaled canvas, but Display.scale() resizes
      // the rendered element — so the cursor drifts off by the scale
      // factor. Compensate by dividing the reported coords by the
      // current scale before forwarding to the server.
      function sendScaledMouse(state) {
        var s = dispGuac.getScale ? dispGuac.getScale() : 1;
        if (s && s !== 1) {
          // Mutate a copy so we don't disturb Guacamole.Mouse's state.
          var scaled = Object.create(state);
          scaled.x = state.x / s;
          scaled.y = state.y / s;
          client.sendMouseState(scaled);
        } else {
          client.sendMouseState(state);
        }
      }
      mouse.onmousedown = mouse.onmouseup = mouse.onmousemove = sendScaledMouse;

      // ---------- Clipboard sync (text only) ----------
      // Remote → Local: when the remote PC copies, Guacamole pushes a stream
      // to client.onclipboard. We read it fully, then write to the local
      // clipboard via the async Clipboard API (requires HTTPS or localhost).
      client.onclipboard = function (stream, mimetype) {
        if (!mimetype.startsWith('text/')) {
          // Skip non-text clipboards (images, files). The stream still
          // needs to be drained or guacd will stall.
          stream.onblob = function () {};
          stream.onend = function () {};
          return;
        }
        var reader = new Guacamole.StringReader(stream);
        var buf = '';
        reader.ontext = function (chunk) { buf += chunk; };
        reader.onend = function () {
          if (!navigator.clipboard || !navigator.clipboard.writeText) return;
          navigator.clipboard.writeText(buf).catch(function () { /* silent */ });
        };
      };

      // Local → Remote: tricky because Guacamole's keyboard layer eats key
      // events on `document`, so the browser's native paste handler often
      // doesn't fire when focus is in the RDP area. Three triggers cover
      // the realistic cases:
      //
      //   1. document-level `paste` (when paste DOES land natively, e.g.
      //      right-click → Paste on the page).
      //   2. Window focus -- if the user copied something in another app
      //      and switched back to this tab, push the clipboard preemptively
      //      so the very next Ctrl+V in the remote already has the text.
      //   3. Explicit Ctrl+V intercept on the Guacamole.Keyboard layer:
      //      read the clipboard, push it, THEN let the V keystroke go to
      //      the remote where it pastes.
      function sendClipboardToRemote(text) {
        if (!text || !client) return;
        var stream = client.createClipboardStream('text/plain');
        var writer = new Guacamole.StringWriter(stream);
        writer.sendText(text);
        writer.sendEnd();
      }
      function pullLocalClipboardAndPush() {
        if (!navigator.clipboard || !navigator.clipboard.readText) return;
        return navigator.clipboard.readText().then(function (text) {
          if (text) sendClipboardToRemote(text);
        }).catch(function () { /* permission denied -- silent */ });
      }
      // (1) Native paste anywhere on the document.
      $(document).on('paste.rdp', function (e) {
        var data = (e.originalEvent.clipboardData || window.clipboardData);
        if (!data) return;
        var text = data.getData('text/plain');
        if (text) sendClipboardToRemote(text);
      });
      // (2) Window focus.
      $(window).on('focus.rdp', pullLocalClipboardAndPush);
      // (3) Ctrl+V intercept. Guacamole.Keyboard's onkeydown already runs
      // before the keystroke is forwarded. We hook it without breaking
      // the existing handler.
      var origKeydown = keyboard.onkeydown;
      keyboard.onkeydown = function (k) {
        // 0x76 = X11 keysym for 'v', 0xFFE3/0xFFE4 = Left/Right Ctrl.
        // We can't easily check modifier state from a single keysym, so
        // listen for the V keysym and assume the user pressed Ctrl too
        // (worst case: an extra clipboard read that costs nothing).
        if (k === 0x76 || k === 0x56) {
          pullLocalClipboardAndPush();
        }
        return origKeydown ? origKeydown(k) : undefined;
      };

      // Tear down on page leave.
      $(window).on('beforeunload.rdp', function () {
        try { client.disconnect(); } catch (e) {}
      });
    } catch (err) {
      console.error('RDP error', err);
      setStatus('RDP error: ' + (err.message || err), 'error');
      showStatusOverlay('Failed to start session: ' + (err.message || err));
    }
  }

  // Kick off once Guacamole is loaded. The ESM script runs async, so
  // wait for either the ready event or the already-set global.
  if (window.Guacamole) {
    startRdp();
  } else {
    window.addEventListener('guacamole-ready', startRdp, { once: true });
  }

  // ---------- MQTT (browser subscriber) ----------
  var mqttClient = null;
  if (window.mqtt && window.mqtt.connect) {
    var url = (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/mqtt';
    mqttClient = window.mqtt.connect(url, {
      clean: true,
      connectTimeout: 4000,
      reconnectPeriod: 2000,
      clientId: 'browser-' + sessionId,
    });
    mqttClient.on('connect', function () {
      setStatus('Notifications connected', 'online');
      mqttClient.subscribe('browser/' + sessionId + '/notify');
      // Auto-shutdown warning topic -- broadcast to every browser watching
      // THIS device's connect page (not addressed to a specific session).
      mqttClient.subscribe('device/' + deviceId + '/notify');
    });
    mqttClient.on('error', function () { setStatus('Notifications error', 'error'); });
    mqttClient.on('message', function (topic, payload) {
      try {
        var msg = JSON.parse(payload.toString());
        if (msg.type === 'shutdown_warning') {
          showShutdownWarningDialog(msg);
        } else if (msg.type === 'camera_status_reply') {
          handleCameraStatusReply(msg.data || {});
        } else if (msg.type === 'webrtc_answer') {
          handleWebRtcAnswer(msg.data || {});
        } else if (msg.type === 'webrtc_ice') {
          handleWebRtcIce(msg.data || {});
        } else if (msg.type === 'webrtc_error') {
          handleWebRtcError(msg.data || {});
        }
      } catch (e) { /* ignore bad payload */ }
    });
  } else {
    setStatus('MQTT client not loaded', 'error');
  }

  // ---------- WebRTC camera ----------
  // Flow:
  //   1. User clicks Start → ping agent: 'is your camera enabled?'
  //   2. Agent replies camera_status_reply {enabled, label} over MQTT.
  //   3. If enabled, build RTCPeerConnection, create offer, send to agent.
  //   4. Agent replies webrtc_answer; ICE candidates trickle both ways.
  //   5. Stream renders in <video>; closing the page tears it down.
  //
  // The agent picks which camera to use — admin chose it once with
  // `agenticlab-agent camera enable`. The browser never picks.

  var pc = null;
  var streamActive = false;

  function setCameraPlaceholder(text) {
    $('#camera-placeholder').text(text).removeClass('hidden');
    var $v = $('#camera-video');
    if ($v[0]) $v[0].srcObject = null;
    $v.addClass('hidden');
  }

  function startCamera() {
    if (streamActive) return;
    setCameraPlaceholder('Checking camera…');
    $('#camera-start').prop('disabled', true);
    $.post('/api/devices/' + deviceId + '/camera/status/', { session_id: sessionId })
      .fail(function (xhr) {
        $('#camera-start').prop('disabled', false);
        setCameraPlaceholder('Off');
        Toast.show((xhr.responseJSON && xhr.responseJSON.error) || 'Camera check failed', 'error');
      });
    // The agent's reply arrives over MQTT and lands in handleCameraStatusReply.
  }

  function handleCameraStatusReply(data) {
    $('#camera-start').prop('disabled', false);
    if (!data.enabled) {
      setCameraPlaceholder('Camera not enabled on this PC');
      Toast.show('Camera is disabled on the agent. Run `agenticlab-agent camera enable` on that PC.', 'error');
      return;
    }
    setCameraPlaceholder('Connecting to ' + (data.label || 'camera') + '…');
    beginWebRtc();
  }

  function beginWebRtc() {
    streamActive = true;
    // No STUN -- both peers are on the same VPN, so their host candidates
    // (10.x.x.x VPN IPs) are directly routable. Skipping STUN avoids a
    // dependency on a public Google service and shaves a roundtrip off
    // session start.
    pc = new RTCPeerConnection({ iceServers: [] });
    pc.ontrack = function (e) {
      var $v = $('#camera-video');
      $v[0].srcObject = e.streams[0];
      $v.removeClass('hidden');
      $('#camera-placeholder').addClass('hidden');
      $('#camera-fullscreen').removeClass('hidden');
    };
    pc.onicecandidate = function (e) {
      if (e.candidate) {
        console.log('[camera] local ICE candidate:', e.candidate.candidate);
        $.post('/api/devices/' + deviceId + '/sdp/', {
          type: 'ice',
          payload: JSON.stringify(e.candidate),
          session_id: sessionId,
        });
      } else {
        console.log('[camera] local ICE gathering complete');
      }
    };
    pc.oniceconnectionstatechange = function () {
      console.log('[camera] ICE state:', pc.iceConnectionState);
    };
    pc.onicegatheringstatechange = function () {
      console.log('[camera] ICE gathering:', pc.iceGatheringState);
    };
    pc.onconnectionstatechange = function () {
      console.log('[camera] PC state:', pc.connectionState);
      if (pc && (pc.connectionState === 'failed' ||
                 pc.connectionState === 'closed' ||
                 pc.connectionState === 'disconnected')) {
        teardownCamera();
      }
    };
    pc.addTransceiver('video', { direction: 'recvonly' });
    pc.createOffer().then(function (offer) {
      return pc.setLocalDescription(offer).then(function () {
        $.post('/api/devices/' + deviceId + '/sdp/', {
          type: 'offer',
          payload: offer.sdp,
          session_id: sessionId,
        });
      });
    }).catch(function (err) {
      Toast.show('WebRTC offer failed: ' + err.message, 'error');
      teardownCamera();
    });
    $('#camera-start').addClass('hidden');
    $('#camera-stop').removeClass('hidden');
  }

  function handleWebRtcAnswer(data) {
    if (!pc || !data.sdp) return;
    var agentCands = (data.sdp.match(/^a=candidate:.*$/gm) || []);
    console.log('[camera] remote answer received, ' + agentCands.length + ' agent candidate(s):');
    agentCands.forEach(function (c) { console.log('  ' + c); });
    pc.setRemoteDescription({ type: 'answer', sdp: data.sdp }).catch(function (e) {
      Toast.show('Bad answer from agent: ' + e.message, 'error');
    });
  }

  function handleWebRtcIce(data) {
    if (!pc || !data.candidate) return;
    console.log('[camera] remote ICE candidate from agent:', data.candidate);
    try {
      pc.addIceCandidate(data.candidate);
    } catch (e) {
      console.warn('[camera] failed to add remote ICE:', e);
    }
  }

  function handleWebRtcError(data) {
    Toast.show(data.error || 'Camera failed on the agent', 'error');
    teardownCamera();
  }

  function teardownCamera() {
    if (pc) {
      try { pc.close(); } catch (e) {}
      pc = null;
    }
    setCameraPlaceholder('Off');
    $('#camera-stop').addClass('hidden');
    $('#camera-start').removeClass('hidden');
    $('#camera-fullscreen').addClass('hidden');
    if (streamActive) {
      $.post('/api/devices/' + deviceId + '/camera/stop/', { session_id: sessionId });
    }
    streamActive = false;
  }

  $('#camera-start').on('click', startCamera);
  $('#camera-stop').on('click', teardownCamera);

  $('#camera-fullscreen').on('click', function () {
    var container = document.getElementById('camera-container');
    if (!container) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen().catch(function (err) {
        Toast.show('Fullscreen failed: ' + err.message, 'error');
      });
    }
  });

  // ---------- Auto-shutdown warning + renew modal ----------
  // Only show one warning dialog at a time -- if MQTT replays the message
  // or two warnings overlap, the second one is ignored.
  var warningOpen = false;

  function updateShutdownCountdown(fireAtIso) {
    var $el = $('#connect-shutdown-countdown');
    if (!$el.length) return;
    if (fireAtIso) {
      $el.attr('data-fire-at', fireAtIso).removeClass('hidden');
    } else {
      $el.attr('data-fire-at', '').addClass('hidden');
    }
    if (window.tickShutdownCountdowns) window.tickShutdownCountdowns();
  }

  function renewShutdown(hours, dlg) {
    $.post('/api/devices/' + deviceId + '/renew-shutdown/', { hours: hours })
      .done(function (r) {
        if (r.ok) {
          updateShutdownCountdown(r.fire_at);
          Toast.show('Renewed for ' + hours + ' hour' + (hours > 1 ? 's' : ''), 'success');
          dlg.hide();
        } else {
          Toast.show(r.error || 'Renew failed', 'error');
        }
      })
      .fail(function (xhr) {
        Toast.show((xhr.responseJSON && xhr.responseJSON.error) || 'Renew failed', 'error');
      });
  }

  function showShutdownWarningDialog(msg) {
    if (warningOpen) return;
    warningOpen = true;
    var fireAtLocal = msg.fire_at ? new Date(msg.fire_at).toLocaleTimeString() : '';
    var body =
      '<div class="space-y-3">' +
        '<p class="text-sm">This PC will <strong>shut down at ' +
          $('<div>').text(fireAtLocal).html() +
        '</strong>.</p>' +
        '<p class="text-xs text-slate-500 dark:text-slate-400">' +
          'Renew to keep working. If you ignore this, the PC will power off when the timer hits zero.' +
        '</p>' +
      '</div>';
    var dlg = new Dialog('Auto-shutdown coming up', body, { size: 'md' });
    dlg.setEvents([{ action: 'hide', callback: function () { warningOpen = false; } }]);
    dlg.setButtons([
      { name: 'Ignore', dismiss: true },
      { name: 'Renew 1h', class: 'bg-orange-500 hover:bg-orange-600 text-white',
        onClick: function () { renewShutdown(1, dlg); } },
      { name: 'Renew 2h', class: 'bg-orange-500 hover:bg-orange-600 text-white',
        onClick: function () { renewShutdown(2, dlg); } },
      { name: 'Renew 4h', class: 'bg-orange-500 hover:bg-orange-600 text-white',
        onClick: function () { renewShutdown(4, dlg); } },
    ]).show();
  }

  // Tear down on page unload, navigation, or tab close.
  $(window).on('beforeunload', function () {
    if (streamActive) teardownCamera();
    if (mqttClient) mqttClient.end(true);
  });

  // ---------- Late-join check ----------
  // The MQTT warnings (warn_shutdown at -1h, warn_shutdown_local at -5min)
  // are pushed once and not retained, so a user who opens the connect page
  // AFTER the warning fired wouldn't see the modal. On load, ask the server
  // if there's a pending shutdown <10 min away and synthesize the modal.
  $.get('/api/devices/' + deviceId + '/pending-shutdown/').done(function (resp) {
    if (!resp || !resp.ok || !resp.fire_at) return;
    var fireAtMs = Date.parse(resp.fire_at);
    if (isNaN(fireAtMs)) return;
    var msUntilFire = fireAtMs - Date.now();
    // Only show on load if within 10 minutes -- otherwise the live MQTT
    // warning at -5min will handle it normally.
    if (msUntilFire > 0 && msUntilFire <= 10 * 60 * 1000) {
      showShutdownWarningDialog({
        fire_at: resp.fire_at,
        scheduled_id: resp.scheduled_id,
        device_id: deviceId,
        device_name: deviceName,
      });
    }
  });

})(jQuery);
